(ns ui.view.transaction-process.target-transactions
  "Transaction processes linked to a generic source target. Opportunities are
   the first consumer, but the component and create flow only depend on a target
   entity and the `:transaction-process/target` reference."
  (:require
   [common.localization :refer [tr]]
   [common.transaction-process.templates.registry :as template-registry]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.view.transaction-process.create-modal :as create-modal]
   [ui.view.transaction-process.util :as tp-util]))

(defonce ^:private create-modal-target*
  ;; The target whose create-modal is open, or nil. Namespace-level rather than
  ;; closed over by `view`, because the button that opens it now lives in the
  ;; module header - rendered by the module framework, outside `view` - while
  ;; the modal itself still renders in the module content. Keyed by target id
  ;; so a stale open flag cannot follow the user to another entity.
  (r/atom nil))

(defn entity-id
  [entity-or-id]
  (or (:db/id entity-or-id) entity-or-id))

(defn- can-start?
  [target-id]
  (and target-id
       (session/transaction-process?)
       (not (session/is-read-only-user?))
       (not (session/is-guest?))))

(defn start-process-button
  "Module-header `+`, matching the other NEV list modules (see
   `frontend.view.entity-viewers.modules.associated-entities`, which supplies
   the same affordance through `:header-tools`). The action keeps its wording
   in the tooltip, since a bare icon cannot say which process it starts.

   The modal renders here rather than in the module content: a collapsed
   module still mounts its content, but inside an `h-0 overflow-hidden`
   wrapper (see `frontend.view.entity-viewers.module-card`), so a modal opened
   from the header would have been clipped out of sight whenever the module
   was collapsed."
  [{:keys [target create-initial-values]}]
  (let [target-id (entity-id target)]
    (when (can-start? target-id)
      [:<>
       [button/secondary-open
        {:tooltip (tr :transaction-process/start-ic-process)
         :on-click (fn [_] (reset! create-modal-target* target-id))}
        [:i.fal.fa-plus-circle.text-md]]
       (when (= target-id @create-modal-target*)
         [create-modal/view
          {:on-close       (fn [] (reset! create-modal-target* nil))
           :initial-values (assoc create-initial-values :target-id target-id)}])])))

(defn linked-processes
  [target-id processes]
  (if target-id
    (->> processes
         (filter (fn [process]
                   (= target-id
                      (entity-id (:transaction-process/target process)))))
         (sort-by :transaction-process/short-name))
    []))

(defn- stage-label
  [process]
  (let [template (template-registry/get-template
                  (:transaction-process/template-kw process))
        stage-id (:transaction-process/stage process)]
    (or (->> (:template/stages template)
             (some (fn [stage]
                     (when (= stage-id (:stage/id stage))
                       (:stage/label stage)))))
        (some-> stage-id name))))

(defn- status-label
  [process]
  (let [status (:transaction-process/status process)]
    (or (tp-util/lookup-keyword-label :transaction-process/status status)
        (some-> status name))))

(defn- process-table
  ;; Semantic tokens rather than raw Tailwind greys, so the table follows the
  ;; theme like the rest of the NEV instead of being pinned to one palette.
  [processes]
  [:div {:class "overflow-x-auto"}
   [:table {:class "w-full text-sm border-collapse"}
    [:thead
     [:tr {:class (str "border-b border-solid border-base-light-border-paler "
                       "text-left text-base-light-text-paler")}
      [:th {:class "py-2 pr-4 font-medium"} (tr :name)]
      [:th {:class "py-2 pr-4 font-medium"} (tr :stage)]
      [:th {:class "py-2 font-medium"} (tr :status)]]]
    [:tbody
     (for [process processes]
       ^{:key (:db/id process)}
       [:tr {:class    (str "border-b border-solid border-base-light-border-paler "
                            "cursor-pointer hover:bg-base-regular")
             :on-click (fn []
                         (routing/open-entity-type :entity.type/transaction-process
                                                   (:db/id process)))}
        [:td {:class "py-3 pr-4 font-medium text-base-light-text"}
         (:transaction-process/short-name process)]
        [:td {:class "py-3 pr-4 text-base-light-text"} (stage-label process)]
        [:td {:class "py-3 text-base-light-text"} (status-label process)]])]]])

(defn view
  [{:keys [target]}]
  (let [target-id (entity-id target)
        processes (linked-processes target-id @session/transaction-processes)]
    ;; Container classes copied from the other NEV list modules
    ;; (`counterparty-diligences`) so the content lines up with theirs rather
    ;; than starting further left.
    [:div {:class "flex flex-col gap-3 pl-1.5 pt-2"}
     (if (seq processes)
       [process-table processes]
       ;; Same classes as the other NEV list modules' empty states (see
       ;; `counterparty-diligences`): not italic, and the semantic paler-text
       ;; token rather than a raw grey.
       [:p {:class "text-base-light-text-paler text-sm m-0"}
        (tr :transaction-process/no-target-transactions)])]))

(defn module-config
  [{:keys [target create-initial-values module-collapsed-path save-module-collapsed!]}]
  (let [module-id :transactions]
    {:module-id              module-id
     :header                 (tr :transaction-process/transactions)
     :header-tools           [start-process-button
                              {:target                target
                               :create-initial-values create-initial-values}]
     :collapsed?             (get-in @session/view-config (module-collapsed-path module-id))
     :save-module-collapsed! save-module-collapsed!
     :content                [view {:target target}]}))

(defn module-configs
  [props]
  (when (session/transaction-process?)
    [(module-config props)]))
